/**
 * AddCommentCard
 *
 * In-chat approval card for an AI-suggested Word comment (add_comment_tool).
 * Visual model mirrors the auto-redline preview card (editPreviewStyles) and its
 * stale state. One card per comment.
 *
 * - "Add comment" → applying (spinner) → applied ("Comment added ✓") / error +
 *   "Retry".
 * - Stale state: disabled, with the auto-redline-style "content changed" notice
 *   (no Retry — retrying can't succeed until the model regenerates). The stale
 *   StaleApplyAttempt is recorded by the parent (MessageBubble), which carries
 *   that context to the next turn.
 * - No reject button — ignoring the card is the rejection; memorySessionManager
 *   reports the pending state.
 */

import * as React from "react";
import { InlineToolEvent } from "../../interfaces/chatSession";
import { CommentApplyStatus } from "../../interfaces/addCommentTypes";
import { insertDocumentComment } from "./services/wordCommentService";
import {
  containerStyle,
  buttonContainerStyle,
  applyButtonStyle,
  spinnerStyle,
  animationKeyframes,
  errorMessageStyle,
} from "../autoRedline/styles/editPreviewStyles";
import { createLogger } from "../../utils/logger";

const log = createLogger("AddCommentCard");

const STALE_MESSAGE =
  "The content has changed since this comment was prepared. Ask me to " +
  "regenerate it against the current text.";

const STALE_INFO_BLOCK_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  margin: "8px 0 4px",
  padding: "12px 14px",
  backgroundColor: "#F5F9FD",
  border: "1px solid #E2EBF5",
  borderRadius: "10px",
  color: "#0F548C",
  fontSize: "14px",
  lineHeight: "1.5",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#333",
  marginBottom: "8px",
};

const commentBodyStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  border: "1px solid #e8e8e8",
  borderRadius: "4px",
  padding: "8px 12px",
  marginBottom: "8px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontSize: "14px",
  lineHeight: "1.6",
  color: "#333",
};

const targetPreviewStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#888",
  fontStyle: "italic",
  marginBottom: "8px",
  wordBreak: "break-word",
};

/** Comment / annotation icon (speech bubble). */
const CommentIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ flexShrink: 0, color: "#106ebe" }}
  >
    <path
      d="M2 3.5C2 2.67 2.67 2 3.5 2h9c.83 0 1.5.67 1.5 1.5v6c0 .83-.67 1.5-1.5 1.5H6l-3 3v-3H3.5C2.67 11 2 10.33 2 9.5v-6Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

function truncate(text: string, maxLength = 120): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 1) + "…";
}

export interface AddCommentCardProps {
  event: InlineToolEvent;
  onStatusChange: (eventId: string, status: CommentApplyStatus, errorMessage?: string) => void;
}

export const AddCommentCard: React.FC<AddCommentCardProps> = ({ event, onStatusChange }) => {
  const data = event.data.addComment;
  if (!data) return null;

  const { result, status, errorMessage } = data;
  const isStale = status === "stale";
  const isApplying = status === "applying";
  const isApplied = status === "applied";
  const hasFailed = status === "failed";

  const handleAddComment = React.useCallback(async () => {
    if (isApplying || isApplied || isStale) return;

    onStatusChange(event.id, "applying");

    // insertDocumentComment never throws (service step 7) — map its result
    // straight to a status. No try/catch needed.
    const insertResult = await insertDocumentComment(result);

    if (insertResult.stale) {
      onStatusChange(event.id, "stale");
    } else if (insertResult.success) {
      onStatusChange(event.id, "applied");
    } else {
      onStatusChange(event.id, "failed", insertResult.error);
    }

    log.debug("Add-comment apply result", {
      eventId: event.id,
      sectionName: result.sectionName,
      stale: insertResult.stale,
      success: insertResult.success,
    });
  }, [event.id, result, onStatusChange, isApplying, isApplied, isStale]);

  // Target preview: anchorPhrase when present, else a truncated paragraph snippet.
  const targetPreview =
    result.anchorPhrase && result.anchorPhrase.length > 0
      ? result.anchorPhrase
      : truncate(result.anchorParagraphText);

  const buttonText = isApplied
    ? "Comment added ✓"
    : isApplying
      ? "Adding..."
      : hasFailed
        ? "Retry"
        : "Add comment";

  const buttonDisabled = isApplying || isApplied || isStale;

  return (
    <div
      // containerStyle (shared with auto-redline edits) only sets marginTop; add a
      // bottom margin so a following card (e.g. thinking) isn't flush against this one.
      style={{ ...containerStyle, marginBottom: "12px" }}
      role="article"
      aria-label={`Suggested comment for ${result.sectionName}`}
    >
      <style>{animationKeyframes}</style>

      {/* Header: comment icon + section name */}
      <div style={headerStyle}>
        <CommentIcon />
        <span>{result.sectionName || "Suggested comment"}</span>
      </div>

      {/* Comment text — visible before applying (QA requirement) */}
      <div style={commentBodyStyle}>{result.commentText}</div>

      {/* Target preview */}
      <div style={targetPreviewStyle}>Target: {targetPreview}</div>

      {/* Stale-state info block (mirrors AutoRedlinePreview). */}
      {isStale && (
        <div style={STALE_INFO_BLOCK_STYLE} role="status">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0, marginTop: "1px" }}
            aria-hidden="true"
          >
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1" />
            <path d="M8 5V8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="11.5" r="1" fill="currentColor" />
          </svg>
          <span>{STALE_MESSAGE}</span>
        </div>
      )}

      {/* Footer */}
      {!isStale && (
        <div style={buttonContainerStyle}>
          {hasFailed && errorMessage && <span style={errorMessageStyle}>{errorMessage}</span>}
          <button
            onClick={handleAddComment}
            disabled={buttonDisabled}
            aria-label={isStale ? STALE_MESSAGE : buttonText}
            style={{
              ...applyButtonStyle,
              backgroundColor: isApplied ? "#28a745" : hasFailed ? "#dc3545" : "#0078d4",
              borderColor: isApplied ? "#28a745" : hasFailed ? "#dc3545" : "#0078d4",
              cursor: buttonDisabled ? "not-allowed" : "pointer",
              opacity: isApplying ? 0.8 : 1,
            }}
          >
            {isApplying && <span style={spinnerStyle} />}
            {buttonText}
          </button>
        </div>
      )}
    </div>
  );
};

export default AddCommentCard;
